import { useCallback, useMemo } from 'react';
import { FormikErrors } from 'formik';
import { OptionTypeBase, ValueType } from 'react-select';
import * as R from 'ramda';

import { PopupNames } from 'core';
import { usePopupStateOperations } from 'core/hooks';

import { ISelectOption } from 'components/atoms';
import { IAddApplicationFieldsValues } from 'molecules';

import {
  TEACHER_TYPE_OPTIONS,
  TEACHER_TYPE_COUNT_FIELDS,
} from 'components/constants';
import { TeacherHireType } from 'components/types';
import { useGetTeacherTypeNames } from '../Teachers';

type AppendApplicationHelpersType = {
  disabledButton: boolean;
  teacherTypes: ISelectOption<string>[];
  /**
   * Employment type the teacher does NOT have yet — the popup then collects its
   * details inline and creates it together with the application. `null` when the
   * picked type already exists (or nothing is picked / types still loading).
   */
  newEmploymentType: TeacherHireType | null;
  typesLoading: boolean;
  handleChange: (
    name: string,
    value?: string | boolean | ISelectOption<string> | null | OptionTypeBase,
  ) => void;
  onClose: () => void;
};

interface IAppendApplicationArguments {
  values: IAddApplicationFieldsValues;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => Promise<void> | Promise<FormikErrors<IAddApplicationFieldsValues>>;
}

export const useAppendApplicationHelpers = ({
  values,
  setFieldValue,
}: IAppendApplicationArguments): AppendApplicationHelpersType => {
  const {
    operations: { hidePopup },
  } = usePopupStateOperations();

  const {
    data: existingTypes,
    loading: typesLoading,
  } = useGetTeacherTypeNames();

  const newEmploymentType: TeacherHireType | null = useMemo(() => {
    const picked = values.teacherType?.value as TeacherHireType | undefined;

    // While the teacher's types are still loading we cannot tell an existing type
    // from a new one — treat it as existing so we never try to create a duplicate.
    if (!picked || typesLoading) {
      return null;
    }

    return existingTypes.includes(picked) ? null : picked;
  }, [values.teacherType, existingTypes, typesLoading]);

  const disabledButton: boolean = useMemo(() => {
    const applicationValues = R.omit(
      ['comment', 'servicePlace', 'riaPrice', 'teacherTypes'],
      values,
    );

    if (!Object.values(applicationValues).every(Boolean)) {
      return false;
    }

    if (!newEmploymentType) {
      return true;
    }

    const typeValues = Object.values(
      values.teacherTypes?.[0]?.[newEmploymentType] || {},
    );

    return (
      typeValues.length >= TEACHER_TYPE_COUNT_FIELDS[newEmploymentType] &&
      typeValues.every(Boolean)
    );
  }, [values, newEmploymentType]);

  const handleChange = useCallback(
    (
      name: string,
      value?:
        | string
        | boolean
        | ISelectOption<string>
        | Date
        | null
        | ValueType<OptionTypeBase, false>,
    ) => {
      setFieldValue(name, value);
    },
    [setFieldValue],
  );

  const onClose = useCallback(
    () => hidePopup(PopupNames.appendApplicationPopup),
    [hidePopup],
  );

  return {
    disabledButton: !disabledButton,
    // Every employment type stays selectable. Teachers added through the quick-add
    // flow have no employment type at all, and filtering the list down to the ones
    // they already have used to leave this required field with zero options — the
    // application could not be created at all. A type the teacher lacks is created
    // together with the application (addTeacherExist), so no filtering here.
    teacherTypes: TEACHER_TYPE_OPTIONS,
    newEmploymentType,
    typesLoading,
    handleChange,
    onClose,
  };
};
